import { ChevronRight, Home } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
}

const routeLabels: Record<string, string> = {
  "/": "Home",
  "/how-it-works": "How It Works",
  "/pricing": "Pricing",
  "/assessment": "Assessment",
  "/results": "Results",
  "/implementation": "Implementation",
  "/payment-success": "Payment Success",
  "/payment-cancelled": "Payment Cancelled",
};

const siteUrl = "https://gamifyme-scorecard.lovable.app";

const Breadcrumbs = ({ items }: BreadcrumbsProps) => {
  const location = useLocation();
  
  // Auto-generate breadcrumbs from current path if not provided
  const breadcrumbItems: BreadcrumbItem[] = items || (() => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [{ label: "Home", href: "/" }];
    
    let currentPath = "";
    for (const segment of pathSegments) {
      currentPath += `/${segment}`;
      const label = routeLabels[currentPath] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
      breadcrumbs.push({ label, href: currentPath });
    }
    
    return breadcrumbs;
  })();

  // Don't show breadcrumbs on home page
  if (location.pathname === "/") {
    return null;
  }

  // Generate JSON-LD structured data
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.label,
      "item": `${siteUrl}${item.href === "/" ? "" : item.href}`
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <nav 
        aria-label="Breadcrumb" 
        className="w-full py-3 px-4 bg-muted/30 border-b border-border/50"
      >
        <div className="max-w-7xl mx-auto">
          <ol 
            className="flex items-center flex-wrap gap-1 text-sm"
            itemScope 
            itemType="https://schema.org/BreadcrumbList"
          >
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              const isFirst = index === 0;
              
              return (
                <li 
                  key={item.href}
                  className="flex items-center"
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem"
                >
                  {index > 0 && (
                    <ChevronRight className="w-4 h-4 mx-1 text-muted-foreground/60 flex-shrink-0" />
                  )}
                  
                  {isLast ? (
                    <span 
                      className="text-foreground font-medium"
                      itemProp="name"
                      aria-current="page"
                    >
                      {item.label}
                    </span>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
                      itemProp="item"
                    >
                      {isFirst && <Home className="w-4 h-4" />}
                      <span itemProp="name">{item.label}</span>
                    </Link>
                  )}
                  
                  <meta itemProp="position" content={String(index + 1)} />
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </>
  );
};

export default Breadcrumbs;
